// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  type MyResearchState,
  type Screener,
} from '@shared/data-access/interfaces/redux/my-research-state';
import {
  getSavedPortfolios,
  getScreeners,
  updateSavedPortfolio,
} from './my-research-thunk';
import { type EtfFilters } from '@shared/data-access/interfaces/api/ai/etf';
import { type MutualFundFilters } from '@shared/data-access/interfaces/api/ai/mutual-funds';
import {
  savedPortfoliosEntityAdapter,
  screenersEntityAdapter,
} from './my-research-selectors';
import { etfsScreenerInitialFilters } from './etf-screener-defaults';
import { mutualFundsScreenerInitialFilters } from './mutual-fund-screener-defaults';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';

export const initialState: MyResearchState = {
  savedPortfolios: savedPortfoliosEntityAdapter.getInitialState({
    ideasTableData: [],
  }),
  screeners: screenersEntityAdapter.getInitialState({
    loading: false,
    mutualFundsFilters: { ...mutualFundsScreenerInitialFilters },
    etfsFilters: { ...etfsScreenerInitialFilters },
  }),
  comparison: {
    benchmark: 'SPY',
    tickers: [],
    w_hist: 365,
    assetsComparison: {
      historical_data: {
        end_date: '',
        start_date: '',
      },
      asset_analysis: [],
      asset_backtest: [],
      corr_matrix: { ticker: [], correlation_cols: {} },
      risk_returns: [],
    },
    assetsInfo: [],
  },
};

export const myResearchSlice = createSlice({
  name: 'myResearch',
  initialState: () => initialState,
  reducers: {
    resetSavedPortfolios: state => {
      savedPortfoliosEntityAdapter.removeAll(state.savedPortfolios);

      state.savedPortfolios.ideasTableData = [];
    },
    removeSavedPortfolio: (state, { payload }: PayloadAction<number>) => {
      savedPortfoliosEntityAdapter.removeOne(state.savedPortfolios, payload);
      /* istanbul ignore next */
      state.savedPortfolios.ideasTableData =
        state.savedPortfolios.ideasTableData.filter(
          item => item.portfolio_id !== payload,
        );
    },
    resetScreeners: state => {
      screenersEntityAdapter.removeAll(state.screeners);
      state.screeners.mutualFundsFilters =
        initialState.screeners.mutualFundsFilters;
      state.screeners.etfsFilters = initialState.screeners.etfsFilters;
    },
    resetMutualFundsFilters: state => {
      state.screeners.mutualFundsFilters =
        initialState.screeners.mutualFundsFilters;
    },
    resetEtfsFilters: state => {
      state.screeners.etfsFilters = initialState.screeners.etfsFilters;
    },
    setScreeners: (state, { payload }: PayloadAction<Screener[]>) => {
      screenersEntityAdapter.setAll(state.screeners, payload);
    },
    setComparisonHist: (state, { payload }: PayloadAction<number>) => {
      state.comparison.w_hist = payload;
    },
    setComparisonTickers: (state, { payload }: PayloadAction<string[]>) => {
      state.comparison.tickers = Array.from(
        new Set(state.comparison.tickers.concat(payload)),
      );
    },
    removeComparisonTickers: (state, { payload }: PayloadAction<string[]>) => {
      state.comparison.tickers = state.comparison.tickers.filter(
        ticker => !payload.includes(ticker),
      );
    },
    setComparisonBenchmark: (state, { payload }: PayloadAction<string>) => {
      state.comparison.benchmark = payload;
    },
    setMutualFundsFilters: (
      state,
      { payload }: PayloadAction<MutualFundFilters>,
    ) => {
      state.screeners.mutualFundsFilters = payload;
    },
    setEtfsFilters: (state, { payload }: PayloadAction<EtfFilters>) => {
      state.screeners.etfsFilters = payload;
    },
  },
  extraReducers: builder => {
    /* istanbul ignore next */
    builder
      .addCase(getSavedPortfolios.fulfilled, (state, { payload }) => {
        savedPortfoliosEntityAdapter.setAll(
          state.savedPortfolios,
          payload.savedPortfolios,
        );

        state.savedPortfolios.ideasTableData = payload.ideas;
      })
      .addCase(updateSavedPortfolio.fulfilled, (state, { payload }) => {
        /* istanbul ignore next */
        savedPortfoliosEntityAdapter.updateOne(state.savedPortfolios, {
          changes: payload.portfolio,
          id: payload.portfolio.portfolio_id,
        });

        /* istanbul ignore next */
        state.savedPortfolios.ideasTableData =
          state.savedPortfolios.ideasTableData.map(idea =>
            idea.portfolio_id === payload.portfolio.portfolio_id
              ? payload.idea
              : idea,
          );
      })
      .addCase(getScreeners.pending, state => {
        state.screeners.loading = true;
      })
      .addCase(getScreeners.fulfilled, (state, { payload }) => {
        state.screeners.loading = false;
        screenersEntityAdapter.setAll(state.screeners, payload);
      })
      .addCase(getScreeners.rejected, state => {
        state.screeners.loading = false;
      })
      .addMatcher(
        assetsApi.endpoints.comparison.matchFulfilled,
        (state, { meta: { arg }, payload: [assetsComparison, assetsInfo] }) => {
          state.comparison.tickers = assetsComparison.asset_analysis
            .map(asset => asset.ticker)
            .filter(ticker =>
              arg.originalArgs.tickers.includes(state.comparison.benchmark)
                ? true
                : ticker !== state.comparison.benchmark,
            );

          state.comparison.assetsComparison = assetsComparison;
          state.comparison.assetsInfo = assetsInfo;
        },
      );
  },
});

export const {
  actions: {
    removeSavedPortfolio,
    resetSavedPortfolios,
    resetScreeners,
    setScreeners,
    setComparisonHist,
    setComparisonTickers,
    setComparisonBenchmark,
    removeComparisonTickers,
    setMutualFundsFilters,
    setEtfsFilters,
    resetEtfsFilters,
    resetMutualFundsFilters,
  },
  reducer,
} = myResearchSlice;
